import {
  createRouter,
  createWebHistory,
  createMemoryHistory,
  NavigationGuardNext,
  RouteLocationNormalized
} from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useSessionStore } from '@/stores/session'
import { useNotificationStore } from '@/stores/notification'
import { ACTION_ROLES } from '@/utils/constants'
import { redirectIfAuthenticated, requireAuthenticatedUser } from '@/router/guards'
import { ROUTES } from '@/router/routes.enum'
import { productRoutes } from '@/modules/products/products.routes'
import { getDefaultRoute } from '@/utils/routes'
import { isNetworkOrTimeoutError } from '@/services/api/base'

const router = createRouter({
  history: import.meta.env.MODE === 'test' ? createMemoryHistory() : createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: '/sign_in',
      name: ROUTES.SIGN_IN,
      beforeEnter: redirectIfAuthenticated,
      component: () => import('@/views/SignInView.vue'),
      meta: {
        redirectNotification: 'You are already signed in'
      }
    },
    {
      path: '/sessions/magic_link/verify',
      name: ROUTES.SEND_MAGIC_LINK,
      beforeEnter: redirectIfAuthenticated,
      component: () => import('@/views/MagicLinkView.vue'),
      meta: {
        redirectNotification: 'Cannot use a magic link when already signed in'
      }
    },
    {
      path: '/invite/set_password',
      name: ROUTES.INVITE_SET_PASSWORD,
      beforeEnter: redirectIfAuthenticated,
      component: () => import('@/views/accept-invite/SetPasswordView.vue'),
      meta: {
        redirectNotification: 'You cannot accept a new user invitation when already signed in'
      }
    },
    {
      path: '/invite/add_authenticator',
      name: ROUTES.INVITE_ADD_AUTHENTICATOR,
      beforeEnter: requireAuthenticatedUser,
      component: () => import('@/views/accept-invite/AddAuthenticatorView.vue'),
      meta: {
        roles: ACTION_ROLES.INVITE_ADD_AUTHENTICATOR
      }
    },
    {
      path: '/invite/add_passkey',
      name: ROUTES.INVITE_ADD_PASSKEY,
      beforeEnter: requireAuthenticatedUser,
      component: () => import('@/views/accept-invite/AddPasskeyView.vue'),
      meta: {
        roles: ACTION_ROLES.INVITE_ADD_PASSKEY
      }
    },
    {
      path: '/user_sessions/auto_sign_in',
      name: ROUTES.AUTO_SIGN_IN,
      beforeEnter: requireAuthenticatedUser,
      component: () => import('@/views/AutoSignInView.vue'),
      meta: {
        roles: ACTION_ROLES.AUTO_SIGN_IN
      }
    },
    {
      path: '/password/reset',
      name: ROUTES.SEND_RESET_PASSWORD_INSTRUCTIONS,
      beforeEnter: redirectIfAuthenticated,
      component: () => import('@/views/SendResetPasswordInstructionsView.vue'),
      meta: {
        redirectNotification: 'Use the Password & Security page to reset your password when already signed in'
      }
    },
    {
      path: '/password/new',
      name: ROUTES.RESET_PASSWORD,
      beforeEnter: redirectIfAuthenticated,
      component: () => import('@/views/ResetPasswordView.vue'),
      meta: {
        redirectNotification: 'Use the Password & Security page to reset your password when already signed in'
      }
    },
    {
      path: '/',
      component: () => import('@/views/BaseView.vue'),
      beforeEnter: (to, _from, next) => redirectToDefaultRoute(to, next),
      children: [
        ...productRoutes,
        // Dummy route for checking if the user is authenticated
        {
          path: '',
          component: () => null,
          beforeEnter: requireAuthenticatedUser
        },
        {
          path: '/orders',
          name: ROUTES.ORDERS,
          component: () => import('@/views/OrdersView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.ORDERS_VIEW
          }
        },
        {
          path: '/orders/:id',
          name: ROUTES.ORDER_DETAILS,
          component: () => import('@/views/OrderDetailsView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.ORDERS_VIEW
          }
        },
        {
          path: '/customers',
          name: ROUTES.CUSTOMERS,
          component: () => import('@/views/CustomersView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.CUSTOMERS_VIEW
          }
        },
        {
          path: '/customers/:id',
          name: ROUTES.CUSTOMER_DETAILS,
          component: () => import('@/views/CustomerDetailsView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.CUSTOMER_DETAILS_VIEW
          }
        },
        {
          path: '/cards',
          name: ROUTES.CARDS,
          component: () => import('@/modules/cards/views/CardsView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.CARD_PASSES_VIEW
          }
        },
        {
          path: '/cards/:id',
          name: ROUTES.CARD_DETAILS,
          component: () => import('@/modules/cards/views/CardDetailsView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.CARD_PASS_DETAILS_VIEW
          }
        },
        {
          path: '/printed-tickets',
          name: ROUTES.PRINTED_TICKETS,
          component: () => import('@/modules/printed-tickets/views/PrintedTicketListingView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.PRINTED_TICKETS_VIEW
          }
        },
        {
          path: '/printed-tickets/:id',
          name: ROUTES.PRINTED_TICKET_DETAILS,
          component: () => import('@/modules/printed-tickets/views/PrintedTicketDetailsView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.PRINTED_TICKET_DETAILS_VIEW
          }
        },
        {
          path: '/decrypt',
          name: ROUTES.DECRYPT,
          component: () => import('@/views/DecryptView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.DECRYPT_VIEW
          }
        },
        {
          path: '/passes',
          name: ROUTES.PASSES,
          component: () => import('@/views/PassesView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.MOBILE_PASS_VIEW
          }
        },
        {
          path: '/passes/:id',
          name: ROUTES.PASS_DETAILS,
          component: () => import('@/views/PassDetailsView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.PASS_DETAILS_VIEW
          }
        },
        {
          path: '/payments',
          name: ROUTES.PAYMENTS,
          component: () => import('@/views/PaymentsView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.PAYMENTS_VIEW
          }
        },
        {
          path: '/refunds',
          name: ROUTES.REFUNDS,
          component: () => import('@/views/RefundsView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.REFUNDS_VIEW
          }
        },
        {
          path: '/scans',
          name: ROUTES.SCANS,
          component: () => import('@/views/ScansView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.SCANS_VIEW
          }
        },
        {
          path: '/scans/pass/:id',
          name: ROUTES.SCANS_BY_PASS,
          component: () => import('@/views/ScansView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.SCANS_VIEW
          }
        },
        {
          path: '/scans/card-pass/:cardPassId',
          name: ROUTES.SCANS_BY_CARD_PASS,
          component: () => import('@/views/ScansView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.SCANS_VIEW
          }
        },
        {
          path: '/reversed-scans',
          name: ROUTES.REVERSED_SCANS,
          component: () => import('@/views/ReversedScansView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.REVERSED_SCANS_VIEW
          }
        },
        {
          path: '/reversed-scans/pass/:id',
          name: ROUTES.REVERSED_SCANS_BY_PASS,
          component: () => import('@/views/ReversedScansView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.REVERSED_SCANS_VIEW
          }
        },
        {
          path: '/reversed-scans/card_pass/:cardPassId',
          name: ROUTES.REVERSED_SCANS_BY_CARD_PASS,
          component: () => import('@/views/ReversedScansView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.REVERSED_SCANS_VIEW
          }
        },
        {
          path: '/users',
          name: ROUTES.USERS,
          component: () => import('@/views/UsersView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.USERS_VIEW
          }
        },
        {
          path: '/users/:id',
          name: ROUTES.USER_DETAILS,
          component: () => import('@/views/UserDetailsView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.USER_DETAILS_VIEW,
            checkByUser: true
          }
        },
        {
          path: '/users/:userId/security',
          name: ROUTES.USER_SECURITY,
          component: () => import('@/views/SecurityView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.USER_SECURITY_VIEW
          }
        },
        {
          path: '/security',
          name: ROUTES.SECURITY,
          component: () => import('@/views/SecurityView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.SECURITY_VIEW
          }
        },
        {
          path: '/reports',
          name: ROUTES.REPORTS,
          component: () => import('@/views/ReportsView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.REPORTS_VIEW
          }
        },
        {
          path: '/pos',
          name: ROUTES.POS_NEW_ORDER,
          component: () => import('@/modules/pos/PointOfSale.view.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.POS_NEW_ORDER_VIEW,
            fullWidthLayout: true,
            showRecentOrders: true,
            showStatusBadges: true,
            whiteBackground: true,
            collapsibleSidebar: true,
            displayName: 'Point-of-sale'
          }
        },
        {
          path: '/settings',
          name: ROUTES.SETTINGS,
          component: () => import('@/views/SettingsView.vue'),
          redirect: {
            name: ROUTES.SETTINGS_BUSINESS
          },
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.SETTINGS_VIEW
          },
          children: [
            {
              path: 'business',
              name: ROUTES.SETTINGS_BUSINESS,
              component: () => import('@/views/settings/BusinessSettingsView.vue'),
              beforeEnter: requireAuthenticatedUser,
              meta: {
                roles: ACTION_ROLES.SETTINGS_BUSINESS_VIEW
              }
            },
            {
              path: 'locations',
              name: ROUTES.SETTINGS_LOCATIONS,
              component: () => import('@/views/settings/LocationsSettingsView.vue'),
              beforeEnter: requireAuthenticatedUser,
              meta: {
                roles: ACTION_ROLES.SETTINGS_LOCATIONS_VIEW
              }
            },
            {
              path: 'salespoints',
              name: ROUTES.SETTINGS_SALESPOINTS,
              component: () => import('@/views/settings/SalespointsSettingsView.vue'),
              beforeEnter: requireAuthenticatedUser,
              meta: {
                roles: ACTION_ROLES.SETTINGS_SALESPOINTS_VIEW
              }
            },
            {
              path: 'printers',
              name: ROUTES.SETTINGS_PRINTERS,
              component: () => import('@/views/settings/PrintersSettingsView.vue'),
              beforeEnter: requireAuthenticatedUser,
              meta: {
                roles: ACTION_ROLES.SETTINGS_PRINTERS_VIEW
              }
            },
            {
              path: 'payment-terminals',
              name: ROUTES.SETTINGS_PAYMENT_TERMINALS,
              component: () => import('@/views/settings/PaymentTerminalsSettingsView.vue'),
              beforeEnter: requireAuthenticatedUser,
              meta: {
                roles: ACTION_ROLES.SETTINGS_PAYMENT_TERMINALS_VIEW
              }
            },
            {
              path: 'vessels',
              name: ROUTES.SETTINGS_VESSELS,
              component: () => import('@/views/settings/VesselsSettingsView.vue'),
              beforeEnter: requireAuthenticatedUser,
              meta: {
                roles: ACTION_ROLES.SETTINGS_VESSELS_VIEW
              }
            },
            {
              path: 'online-sales',
              name: ROUTES.SETTINGS_ONLINE_SALES,
              component: () => import('@/views/settings/OnlineSalesSettingsView.vue'),
              beforeEnter: requireAuthenticatedUser,
              meta: {
                roles: ACTION_ROLES.SETTINGS_ONLINE_SALES_VIEW
              }
            },
            {
              path: 'system',
              name: ROUTES.SETTINGS_SYSTEM,
              component: () => import('@/views/settings/SystemSettingsView.vue'),
              beforeEnter: requireAuthenticatedUser,
              meta: {
                roles: ACTION_ROLES.SETTINGS_SYSTEM_VIEW
              }
            },
            {
              path: 'pos',
              name: ROUTES.SETTINGS_POS,
              component: () => import('@/views/settings/PosSettingsView.vue'),
              beforeEnter: requireAuthenticatedUser,
              meta: {
                roles: ACTION_ROLES.SETTINGS_POS_VIEW
              }
            }
          ]
        },
        {
          path: '/sign_out',
          name: ROUTES.SIGN_OUT,
          component: () => import('@/views/SignOutView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.SIGN_OUT
          }
        },
        {
          path: '/coupons',
          name: ROUTES.COUPONS,
          component: () => import('@/modules/coupons/views/CouponListingView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.COUPONS_VIEW
          }
        },
        {
          path: '/coupons/:id',
          name: ROUTES.COUPON_DETAILS,
          component: () => import('@/modules/coupons/views/CouponDetailsView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.COUPONS_VIEW
          }
        },
        {
          path: '/qr_codes',
          name: ROUTES.QR_CODES,
          component: () => import('@/views/QrCodesView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.QR_CODES_VIEW
          }
        },
        {
          path: '/validators',
          name: ROUTES.VALIDATORS,
          component: () => import('@/views/ValidatorsView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.VALIDATORS_VIEW
          }
        },
        {
          path: '/validators/:id',
          name: ROUTES.VALIDATOR_DETAILS,
          component: () => import('@/views/ValidatorDetailsView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.VALIDATOR_DETAILS_VIEW
          }
        },
        {
          path: '/orders_test_page',
          name: ROUTES.ORDERS_TEST_PAGE,
          component: () => import('@/views/OrdersTestPageView.vue'),
          beforeEnter: requireAuthenticatedUser,
          meta: {
            roles: ACTION_ROLES.ORDERS_TEST_PAGE_VIEW
          }
        }
      ]
    },
    {
      path: '/404',
      name: ROUTES.NOT_FOUND,
      component: () => import('@/views/NotFoundView.vue')
    },
    {
      path: '/:pathMatch(.*)*',
      redirect: '/404'
    }
  ]
})

router.beforeEach(async (_to, _from, next) => {
  const authStore = useAuthStore()
  const sessionStore = useSessionStore()
  authStore.loadTokenFromCookie()
  const pingResult = await sessionStore.ping()

  if (!isNetworkOrTimeoutError(pingResult.errorCode)) {
    authStore.isAuthenticated = !!pingResult.success
  }

  next()
})

router.afterEach(() => {
  const notificationStore = useNotificationStore()
  notificationStore.clearDisplayed()
})

function redirectToDefaultRoute(to: RouteLocationNormalized, next: NavigationGuardNext) {
  const redirect = to.path === '/'
  const route = getDefaultRoute()

  if (redirect && route !== '/') next({ name: route })
  else next()
}

export default router
